import type { MediaPlayerInstance } from "@vidstack/react";
import type { ComponentProps } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import { PostVideoPlayer } from "@/components/shared/media-player/post-video-player";
import { StopPropagationWrapper } from "@/components/shared/stop-propagation-wrapper";
import { useLayout } from "@/contexts/layout-context";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { AutoplayType } from "@/utils/constants";
import { cn } from "@/utils/lib";
import type { PostMediaMeta } from "@/utils/types";

import {
  isInterruptedPlayRequest,
  resolveVideoAutoplayPreference,
  unregisterVideoAutoplayPlayer,
  updateVideoAutoplayPlayer,
} from "./video-autoplay";

type PlayerProps = {
  url: string;
  autoplay?: AutoplayType;
  className?: string;
  wrapperClassName?: string;
  mediaMeta?: PostMediaMeta;
  onPlayChange?: (playing: boolean) => void;
  postHash?: string;
  playerProps?: Partial<ComponentProps<typeof PostVideoPlayer>>;
};

type PostVideoSectionProps = Omit<PlayerProps, "url"> & {
  videoUrls: string[];
};

export const VideoPlayer = ({
  url,
  autoplay = AutoplayType.NEVER,
  className,
  wrapperClassName,
  mediaMeta,
  postHash,
  onPlayChange,
  playerProps = {},
}: PlayerProps) => {
  const { onCanPlay: onCanPlayProp, ...restPlayerProps } = playerProps;
  const autoplayPlayerId = useId();
  const playerApiRef = useRef<MediaPlayerInstance>(null!);
  const autoplayAttemptRef = useRef(0);
  const [loaded, setLoaded] = useState(false);
  const {
    mediaMuted,
    mediaVolume,
    prevPlayingElementRef,
    videoAutoplayEnabled,
  } = useLayout();
  const effectiveAutoplay = resolveVideoAutoplayPreference(
    autoplay,
    videoAutoplayEnabled,
  );

  const {
    element: containerElement,
    isIntersecting,
    ref: containerRef,
  } = useIntersectionObserver({
    rootMargin: "-40% 0px -40% 0px",
    pool: "video-player",
  });

  const playAutoplay = useCallback(() => {
    const api = playerApiRef.current;
    if (!api) return;

    const attemptId = ++autoplayAttemptRef.current;
    const canRetry = () => autoplayAttemptRef.current === attemptId;

    api.play().catch((error) => {
      if (isInterruptedPlayRequest(error) || !canRetry()) {
        return;
      }

      if (!mediaMuted && mediaVolume > 0) {
        return;
      }

      api.muted = true;

      if (canRetry()) {
        api.play().catch(() => {});
      }
    });
  }, [mediaMuted, mediaVolume]);
  const pauseAutoplay = useCallback(() => {
    autoplayAttemptRef.current += 1;

    const api = playerApiRef.current;
    if (!api || api.paused) return;

    api.pause().catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      autoplayAttemptRef.current += 1;
      unregisterVideoAutoplayPlayer(autoplayPlayerId);
    };
  }, [autoplayPlayerId]);

  useEffect(() => {
    if (effectiveAutoplay === AutoplayType.NEVER) {
      unregisterVideoAutoplayPlayer(autoplayPlayerId);
      return;
    }

    updateVideoAutoplayPlayer(autoplayPlayerId, {
      element: containerElement,
      autoplay: effectiveAutoplay,
      loaded,
      isIntersecting,
      play: playAutoplay,
      pause: pauseAutoplay,
      isPaused: () => playerApiRef.current?.paused ?? true,
      isFullscreen: () => Boolean(document.fullscreenElement),
      getActiveElement: () => prevPlayingElementRef.current,
      getMediaElement: () =>
        (
          playerApiRef.current as
            | (MediaPlayerInstance & { el?: HTMLElement })
            | null
        )?.el?.querySelector("video") ?? null,
    });
  }, [
    autoplayPlayerId,
    containerElement,
    effectiveAutoplay,
    isIntersecting,
    loaded,
    pauseAutoplay,
    playAutoplay,
    prevPlayingElementRef,
  ]);

  return (
    <StopPropagationWrapper
      ref={containerRef}
      className={cn("relative flex flex-col gap-1", wrapperClassName)}
    >
      <PostVideoPlayer
        src={url}
        ref={playerApiRef}
        loop
        mediaMeta={mediaMeta}
        postHash={postHash}
        onCanPlay={(...args) => {
          setLoaded(true);
          onCanPlayProp?.(...args);
        }}
        onPlayChange={onPlayChange}
        className={className}
        {...restPlayerProps}
      />
    </StopPropagationWrapper>
  );
};

export const PostVideoSection = ({
  videoUrls,
  autoplay = AutoplayType.NEVER,
  ...props
}: PostVideoSectionProps) => {
  return videoUrls.map((url, index) => (
    <VideoPlayer
      key={url}
      url={url}
      // Only play the first video, the other's will be hidden,
      // and we don't want to autoplay multiple videos at once
      autoplay={
        index === 0 && autoplay !== AutoplayType.NEVER
          ? autoplay
          : AutoplayType.NEVER
      }
      {...props}
    />
  ));
};
